<template>
    <div class="theme-widget-preview">
        <div ref="host" class="theme-widget-preview-canvas" :aria-label="`${widget.label} widget preview`"></div>
        <div v-if="error" class="theme-widget-preview-error">{{ error }}</div>
    </div>
</template>

<script>
import Konva from 'konva';
import { objectTypelookup } from '../utils/lookupTables';

const STATE_NAMES = new Set(['pressed', 'checked', 'focused', 'edited', 'disabled']);

/**
 * The widget classes assume their Konva layer is the designer's HaspObjectLayer
 * and call straight into its canvas-management API — redraw, history, object
 * menu, transformer, page-0 overlay, badge connections. ObjectGroup's
 * refreshAllStyles(), which every state preview runs through, ends on
 * layer.requestRender(); a bare Konva.Layer has no such method, so the first
 * setPreviewState() threw and each card rendered the error instead of the
 * widget. This is that contract reduced to what a static preview needs: redraw
 * for real, no-op everything that only means something on the editing canvas.
 */
class PreviewLayer extends Konva.Layer {
    requestRender() { this.batchDraw(); }
    layerCallback() { }
    updateObjectMenu() { }
    updatePageZeroOverlay() { }
    updateTransformer() { }
    setSelectedNode() { }
    transformStart() { }
    transform() { }
    transformEnd() { }
    refreshBadgeRings() { }
    refreshBadgeConnections() { }
    refreshBadgeConnectionsForParent() { }
    refreshConnectionsForBadgeValue() { }
    requestConnectionAnimation() { }
    startBadgeConnectionDrag() { }
    updateBadgeConnectionDrag() { }
    finishBadgeConnectionDrag() { }
}

function sizeFor(type) {
    if (['arc', 'badge', 'cpicker', 'gauge', 'linemeter', 'spinner'].includes(type)) {
        return { width: 92, height: 92, x: 64, y: 12 };
    }
    if (['bar', 'slider', 'switch'].includes(type)) {
        return { width: 198, height: type === 'switch' ? 42 : 28, x: 11, y: 38 };
    }
    if (['qrcode', 'led'].includes(type)) {
        return { width: 76, height: 76, x: 72, y: 18 };
    }
    if (type === 'line') return { width: 210, height: 74, x: 5, y: 18 };
    if (type === 'checkbox') return { width: 190, height: 44, x: 14, y: 39 };
    return { width: 210, height: 76, x: 5, y: 22 };
}

function previewConfig(type, widget, theme) {
    const size = sizeFor(type);
    const checked = widget.previewState === 'checked';
    const config = {
        obj: type,
        x: size.x,
        y: size.y,
        width: size.width,
        height: size.height,
        haspid: 1,
        show_id: false,
        text: widget.example || widget.label,
        value_str: '42',
        value: 42,
        val: checked ? 1 : 64,
        checked,
        min: 0,
        max: 100,
        start_angle: 135,
        end_angle: 405,
        angle: 240,
        rotation: 0,
        line_count: 18,
        label_count: 5,
        options: 'One\nTwo\nThree',
        rows: 3,
        btn_pos: 1,
        type: 1,
        points: [0, 12, 210, 62],
        _previewTheme: theme,
    };

    // The live widget classes use this value for their initial visual state;
    // the StyleResolver state preview is applied after the widget is mounted.
    if (type === 'switch') config.val = checked ? 1 : 0;
    if (type === 'led') config.val = 255;
    if (type === 'btnmatrix') config.options = ['One', 'Two', '\n', 'Three', 'Four'];
    if (type === 'chart') config.series = ['#38bdf8', '#fbbf24', '#f472b6'];
    if (type === 'chart') config.type = 2;
    return config;
}

export default {
    name: 'ThemeWidgetPreview',
    props: {
        widget: { type: Object, required: true },
        theme: { type: Object, required: true },
        state: { type: String, default: 'default' },
    },
    data() {
        return { stage: null, layer: null, widgetNode: null, error: '' };
    },
    mounted() {
        this.renderWidget();
    },
    beforeUnmount() {
        this.destroyWidget();
    },
    watch: {
        theme: { deep: true, handler() { this.renderWidget(); } },
        state() { this.renderWidget(); },
    },
    methods: {
        renderWidget() {
            if (!this.$refs.host) return;
            this.destroyWidget();
            this.error = '';

            const type = this.widget.type;
            const WidgetClass = objectTypelookup[type];
            if (!WidgetClass) {
                this.error = 'No canvas renderer available.';
                return;
            }

            try {
                const config = previewConfig(type, { ...this.widget, previewState: this.state }, this.theme);
                this.stage = new Konva.Stage({ container: this.$refs.host, width: 220, height: 120 });
                this.layer = new PreviewLayer({ listening: false });
                this.stage.add(this.layer);
                this.widgetNode = new WidgetClass(config);
                this.layer.add(this.widgetNode);

                if (STATE_NAMES.has(this.state)) this.widgetNode.setPreviewState?.(this.state);

                // HaspSwitch's knob position is based on its drag position, so
                // synchronize it explicitly for the checked sample.
                if (type === 'switch' && this.state === 'checked') this.widgetNode.knob.x(this.widgetNode.rect.width() - this.widgetNode.knob.height() - 2);
                this.layer.draw();
            } catch (error) {
                this.error = error?.message || 'Widget preview could not be rendered.';
                this.destroyWidget();
            }
        },
        destroyWidget() {
            this.widgetNode?.destroy();
            this.widgetNode = null;
            this.layer?.destroy();
            this.layer = null;
            this.stage?.destroy();
            this.stage = null;
        },
    },
};
</script>

<style scoped>
.theme-widget-preview { position: relative; min-height: 120px; overflow: hidden; border: 1px solid #475569; border-radius: 4px; background: #0f172a; }
.theme-widget-preview-canvas { width: 220px; height: 120px; margin: 0 auto; }
.theme-widget-preview-error { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 10px; color: #fca5a5; font-size: 10px; text-align: center; }
</style>
